import ApiService from './api.service'
import type {
  IntervenantResponse,
  IntervenantListResponse,
  IntervenantSearchResponse,
  IntervenantStatisticsResponse,
  IntervenantMinimalListResponse,
  IntervenantData,
  IntervenantSearchOptions
} from '@/types/services'

/**
 * Service for managing project intervenants (stakeholders/participants)
 */
class IntervenantsService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Intervenants Error'
      }
    })
  }

  /**
   * Fetch intervenants for a specific project
   */
  async getIntervenants(projectId: string): Promise<IntervenantListResponse | null> {
    return this.get(`/api/project/${projectId}/intervenants`)
  }

  /**
   * Get a specific intervenant by ID
   */
  async getIntervenantById(projectId: string, intervenantId: string): Promise<IntervenantResponse | null> {
    return this.get(`/api/project/${projectId}/intervenants/${intervenantId}`)
  }

  /**
   * Create a new intervenant
   */
  async createIntervenant(projectId: string, intervenantData: IntervenantData): Promise<IntervenantResponse | null> {
    return this.post(`/api/project/${projectId}/intervenants`, intervenantData)
  }

  /**
   * Update an existing intervenant
   */
  async updateIntervenant(
    projectId: string, 
    intervenantId: string, 
    intervenantData: IntervenantData
  ): Promise<IntervenantResponse | null> {
    return this.put(`/api/project/${projectId}/intervenants/${intervenantId}`, intervenantData)
  }

  /**
   * Delete an intervenant
   */
  async deleteIntervenant(projectId: string, intervenantId: string): Promise<IntervenantResponse | null> {
    return this.delete(`/api/project/${projectId}/intervenants/${intervenantId}`)
  }

  /**
   * Search intervenants by term
   */
  async searchIntervenants(
    projectId: string, 
    searchTerm: string, 
    options: IntervenantSearchOptions = {}
  ): Promise<IntervenantSearchResponse | null> {
    const { page = 0, size = 10 } = options
    return this.get(`/api/project/${projectId}/intervenants/search`, {
      q: searchTerm,
      page,
      size
    })
  }

  /**
   * Get a minimal list of intervenants (id and name only)
   */
  async getIntervenantsList(projectId: string): Promise<IntervenantMinimalListResponse | null> {
    return this.get(`/api/project/${projectId}/intervenants/list`)
  }
}

// Export an instance of the service
export default new IntervenantsService()
